import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowUpCircle,
  Bug,
  CreditCard,
  Flag,
  KeyRound,
  Mail,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type SubscriptionNeedingAttention,
  useMetricsSubscriptionsNeedingAttention,
} from '@/shared/api/admin-metrics';
import { type AdminOverview, useAdminOverview } from '@/shared/api/admin-overview';
import { fmtAgo, fmtBytes, fmtDate, fmtInt } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { Card, ErrorBanner, Muted, PageHeader } from '@/shared/ui/kit';

import { SignupsChart } from './signups-chart';

type TileTarget = '/users' | '/teams' | '/upgrade-requests' | '/errors' | '/emails' | '/metrics' | '/subscriptions';

export function DashboardPage() {
  const { data: o, isLoading, error, refetch } = useAdminOverview();
  const { data: attention } = useMetricsSubscriptionsNeedingAttention();

  return (
    <AdminLayout>
      <PageHeader
        title="Overview"
        description={
          o ? (
            <>
              Platform at a glance · updated {fmtAgo(o.generatedAt)} ·{' '}
              <Link to="/metrics" className="text-orange-400 hover:underline">
                Revenue & storage metrics →
              </Link>
            </>
          ) : (
            'Platform at a glance.'
          )
        }
      />

      {error ? <ErrorBanner error={error} onRetry={() => void refetch()} /> : null}

      {/* Action items first: things a person has to do something about. */}
      <section className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3" aria-label="Needs action">
        <ActionTile
          to="/upgrade-requests"
          icon={<ArrowUpCircle size={15} />}
          label="Pending upgrade requests"
          value={o?.billing.pendingUpgradeRequests}
          loading={isLoading}
          hot={(o?.billing.pendingUpgradeRequests ?? 0) > 0}
          hint="Approve to grant the plan"
        />
        <ActionTile
          to="/errors"
          icon={<Bug size={15} />}
          label="Open error groups (24h)"
          value={o?.errors.open24h}
          loading={isLoading}
          hot={(o?.errors.open24h ?? 0) > 0}
          hint={o ? `${fmtInt(o.errors.open7d)} open in 7 days` : undefined}
        />
        <ActionTile
          to="/emails"
          icon={<Mail size={15} />}
          label="Failed emails (7d)"
          value={o?.email.failed7d}
          loading={isLoading}
          hot={(o?.email.failed7d ?? 0) > 0}
          hint={o ? `${fmtInt(o.email.queued)} queued now` : undefined}
        />
      </section>

      <section className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" aria-label="Key numbers">
        <Stat label="Users" value={o?.users.total} loading={isLoading} to="/users"
          sub={o ? `${fmtInt(o.users.unverified)} unverified` : undefined} />
        <Stat label="Active 7d" value={o?.users.active7d} loading={isLoading} to="/users"
          sub={o ? `${fmtInt(o.users.active30d)} in 30 days` : undefined}
          title="Users who created or changed something (tasks, projects, teams) in the window" />
        <Stat label="Teams" value={o?.workspace.sharedTeams} loading={isLoading} to="/teams"
          sub={o ? `+ ${fmtInt(o.workspace.teams - o.workspace.sharedTeams)} personal` : undefined} />
        <Stat label="Projects" value={o?.workspace.projects} loading={isLoading} to="/teams"
          sub={o ? `${fmtInt(o.workspace.tasksCreated)} tasks created` : undefined} />
        <Stat label="Emails sent 7d" value={o?.email.sent7d} loading={isLoading} to="/emails"
          sub={o ? `${fmtInt(o.email.sent24h)} today · ${fmtInt(o.email.sent30d)} / 30d` : undefined} />
        <Stat label="Storage used" display={o ? fmtBytes(o.workspace.storageBytes) : undefined} loading={isLoading}
          to="/metrics" sub={o ? `${fmtInt(o.billing.activeSubscriptions)} paid/trial subs` : undefined} />
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Signups, last 30 days" actions={<SignupTotals o={o} />}>
          {o ? <SignupsChart days={o.signupsDaily} /> : <Muted>{isLoading ? 'Loading…' : 'No data.'}</Muted>}
        </Card>

        <Card title="Quick actions">
          <div className="flex flex-col gap-1.5">
            <QuickLink to="/upgrade-requests" icon={<ArrowUpCircle size={14} />}>Review upgrade requests</QuickLink>
            <QuickLink to="/users" icon={<Users size={14} />}>Find a user</QuickLink>
            <QuickLink to="/subscriptions/grant" icon={<CreditCard size={14} />}>Grant a plan manually</QuickLink>
            <QuickLink to="/managed-keys" icon={<KeyRound size={14} />}>Managed AI keys</QuickLink>
            <QuickLink to="/flags" icon={<Flag size={14} />}>Feature flags</QuickLink>
          </div>
        </Card>

        <Card
          className="lg:col-span-3"
          title={
            <span className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-amber-400" /> Subscriptions needing attention
              {attention?.length ? <span className="text-xs text-amber-300">({attention.length})</span> : null}
            </span>
          }
        >
          {attention === undefined ? (
            <Muted>Loading…</Muted>
          ) : attention.length === 0 ? (
            <Muted>Nothing needs attention — no expiring trials or past-due subscriptions.</Muted>
          ) : (
            <ul className="divide-y divide-neutral-800">
              {attention.slice(0, 8).map((s) => (
                <AttentionRow key={s.id} s={s} />
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}

function SignupTotals({ o }: { o: AdminOverview | undefined }) {
  if (!o) return null;
  return (
    <span className="text-xs text-neutral-400">
      today <b className="text-neutral-200">{fmtInt(o.users.signupsToday)}</b> · 7d{' '}
      <b className="text-neutral-200">{fmtInt(o.users.signups7d)}</b> · 30d{' '}
      <b className="text-neutral-200">{fmtInt(o.users.signups30d)}</b>
    </span>
  );
}

function ActionTile(props: {
  to: TileTarget;
  icon: ReactNode;
  label: string;
  value: number | undefined;
  loading: boolean;
  hot: boolean;
  hint?: string | undefined;
}) {
  return (
    <Link
      to={props.to}
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
        props.hot
          ? 'border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10'
          : 'border-neutral-800 bg-neutral-900 hover:border-neutral-700',
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-xs text-neutral-400">
          <span className={props.hot ? 'text-amber-400' : 'text-neutral-500'}>{props.icon}</span>
          {props.label}
        </div>
        {props.hint ? <div className="mt-1 truncate text-xs text-neutral-500">{props.hint}</div> : null}
      </div>
      <div className={cn('text-2xl font-semibold tabular-nums', props.hot ? 'text-amber-300' : 'text-neutral-300')}>
        {props.loading ? '…' : fmtInt(props.value)}
      </div>
    </Link>
  );
}

function Stat(props: {
  label: string;
  value?: number | undefined;
  display?: string | undefined;
  sub?: string | undefined;
  loading: boolean;
  to: TileTarget;
  title?: string | undefined;
}) {
  return (
    <Link
      to={props.to}
      title={props.title}
      className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 transition-colors hover:border-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 sm:p-4"
    >
      <div className="text-xs text-neutral-500">{props.label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums text-neutral-100">
        {props.loading ? '…' : (props.display ?? fmtInt(props.value))}
      </div>
      {props.sub ? <div className="truncate text-[11px] text-neutral-500">{props.sub}</div> : null}
    </Link>
  );
}

function AttentionRow(props: { s: SubscriptionNeedingAttention }) {
  const s = props.s;
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <div className="truncate text-sm text-neutral-200">{s.customerLabel}</div>
        <div className="text-xs text-neutral-500">
          {s.planCode} ·{' '}
          <span className={s.reason === 'past_due' ? 'text-red-400' : 'text-amber-400'}>
            {s.reason === 'past_due' ? 'past due' : `trial ends ${fmtDate(s.attentionAt)}`}
          </span>
        </div>
      </div>
      <Link
        to="/subscriptions/$id"
        params={{ id: s.id }}
        className="shrink-0 rounded-md px-2 py-1 text-xs text-neutral-300 ring-1 ring-neutral-700 hover:bg-neutral-800"
      >
        Open
      </Link>
    </li>
  );
}

function QuickLink(props: {
  to: '/subscriptions/grant' | '/upgrade-requests' | '/managed-keys' | '/flags' | '/users';
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Link
      to={props.to}
      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-300 ring-1 ring-neutral-800 hover:bg-neutral-800/60 hover:text-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
    >
      <span className="text-neutral-400">{props.icon}</span>
      {props.children}
    </Link>
  );
}
